import {FC, KeyboardEvent} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'

import {
  RenderIf,
  WindowsIcon,
  AppleIcon,
  LoadingSpinner,
  ArrowRightSmIcon,
  ReleaseNotesIcon,
  ArrowUpRightIcon,
} from 'components'
import {InstallerType, SliceStatus} from 'interfaces'
import {colorCodes, downloadLinks} from 'definitions/constant'

type InstallerOption = {
  labelKey: string
  key: InstallerType
}

interface InstallerDownloadMenuProps {
  options: InstallerOption[]
  isTopBarToBeBlueColor: boolean
  downloadInstallerStatus: SliceStatus
  selectedBundle: InstallerType | ''
  onDownload: (bundle: InstallerType) => void
}

const InstallerDownloadMenu: FC<InstallerDownloadMenuProps> = ({
  options,
  isTopBarToBeBlueColor,
  downloadInstallerStatus,
  selectedBundle,
  onDownload,
}) => {
  const {t} = useTranslation(['common'])
  const hasWindowsArmOption = options.some(
    option => option.key === InstallerType.WindowsArm,
  )

  const handleInstallerKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    bundle: InstallerType,
    isDisabled: boolean,
  ) => {
    if (isDisabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      onDownload(bundle)
    }
  }

  return (
    <div
      id="installer-download"
      className={classNames(
        'pt-4 pb-1 border-l-0.5 border-b-0.5 border-r-0.5 border-gray-400 z-50 absolute right-24 w-64',
        {
          'bg-dope-black': !isTopBarToBeBlueColor,
          'bg-dope-teal-100': isTopBarToBeBlueColor,
        },
      )}
    >
      <p className="text-sm karla font-normal pb-2 px-6 border-b-0.5 border-gray-1200">
        {t('common:ENDPOINT_CLIENT')}
      </p>
      {options.map(option => {
        const label = t(option.labelKey)
        const isWindowsOption = option.key === InstallerType.Windows
        const shouldHideBorder = isWindowsOption && hasWindowsArmOption
        const isInstallerDownloading =
          downloadInstallerStatus === SliceStatus.pending &&
          selectedBundle === option.key
        const isDisabled = isInstallerDownloading

        return (
          <div
            className={classNames(
              'py-4 px-6 flex flex-row justify-between items-center h-8 border-gray-1200',
              {
                'cursor-pointer': !isDisabled,
                'cursor-not-allowed opacity-60': isDisabled,
                'border-b-0': shouldHideBorder,
                'border-b-1': !shouldHideBorder,
                'hover:bg-gray-800': !isTopBarToBeBlueColor && !isDisabled,
                'hover:bg-dope-teal-300': isTopBarToBeBlueColor && !isDisabled,
              },
            )}
            key={option.key}
            data-cy={`download-${option.key}`}
            onClick={event => {
              if (isDisabled) return
              event.stopPropagation()
              onDownload(option.key)
            }}
            onKeyDown={event =>
              handleInstallerKeyDown(event, option.key, isDisabled)
            }
            role="button"
            aria-label={t('common:DOWNLOAD_INSTALLER_ARIA', {label})}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : 0}
          >
            <div className="flex flex-row items-center gap-2 flex-1">
              <RenderIf
                isTrue={
                  isWindowsOption || option.key === InstallerType.WindowsArm
                }
              >
                <WindowsIcon />
              </RenderIf>
              <RenderIf isTrue={option.key === InstallerType.Mac}>
                <AppleIcon />
              </RenderIf>
              <p className="karla font-normal text-xs text-white underline decoration-0 whitespace-nowrap">
                {label}
              </p>
            </div>
            <RenderIf isTrue={isInstallerDownloading}>
              <span
                className="mr-2"
                data-cy={`download-${option.key}-spinner`}
                onMouseDown={event => event.stopPropagation()}
              >
                <LoadingSpinner
                  type="TailSpin"
                  color={colorCodes.white}
                  height={12}
                  width={12}
                />
              </span>
            </RenderIf>
            <RenderIf isTrue={!isInstallerDownloading}>
              <ArrowRightSmIcon classes="cursor-pointer flex-shrink-0" />
            </RenderIf>
          </div>
        )
      })}
      <Link
        className={classNames(
          'py-4 pl-5 pr-6 flex justify-between items-center h-8 cursor-pointer',
          {
            'hover:bg-gray-800': !isTopBarToBeBlueColor,
            'hover:bg-dope-teal-300': isTopBarToBeBlueColor,
          },
        )}
        to={{pathname: downloadLinks.releaseNotes}}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('common:OPEN_ENDPOINT_CLIENT_RELEASE_NOTES')}
      >
        <div className="flex flex-row items-center gap-1 flex-1">
          <ReleaseNotesIcon />
          <p className="karla text-xs text-white underline decoration-0 whitespace-nowrap">
            {t('common:RELEASE_NOTES')}
          </p>
        </div>

        <span className="flex-shrink-0 cursor-pointer">
          <ArrowUpRightIcon />
        </span>
      </Link>
    </div>
  )
}

export default InstallerDownloadMenu
